import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../lib/AuthContext';
import { Database, LogIn, Eye, EyeOff, Loader2 } from 'lucide-react';

const HCAPTCHA_SITE_KEY = import.meta.env.VITE_HCAPTCHA_SITE_KEY;
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1']);
const IS_LOCALHOST = LOCAL_HOSTNAMES.has(window.location.hostname);
const HCAPTCHA_ENABLED = !IS_LOCALHOST && import.meta.env.VITE_HCAPTCHA_ENABLED !== 'false';

function LoginPage() {
    const { login } = useAuth();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [captchaToken, setCaptchaToken] = useState('');
    const captchaRef = useRef(null);
    const captchaWidgetId = useRef(null);

    // Render hCaptcha when the script loads
    useEffect(() => {
        if (!HCAPTCHA_ENABLED) {
            return;
        }

        if (!HCAPTCHA_SITE_KEY) {
            setError('Captcha is not configured. Please contact the administrator.');
            return;
        }

        const renderCaptcha = () => {
            if (window.hcaptcha && captchaRef.current && captchaWidgetId.current === null) {
                captchaWidgetId.current = window.hcaptcha.render(captchaRef.current, {
                    sitekey: HCAPTCHA_SITE_KEY,
                    callback: (token) => setCaptchaToken(token),
                    'expired-callback': () => setCaptchaToken(''),
                    'error-callback': () => setCaptchaToken(''),
                    theme: 'light',
                    size: 'normal',
                });
            }
        };

        // If hcaptcha is already loaded
        if (window.hcaptcha) {
            renderCaptcha();
        } else {
            // Wait for the script to load
            const interval = setInterval(() => {
                if (window.hcaptcha) {
                    clearInterval(interval);
                    renderCaptcha();
                }
            }, 200);
            return () => clearInterval(interval);
        }
    }, []);

    const resetCaptcha = useCallback(() => {
        if (window.hcaptcha && captchaWidgetId.current !== null) {
            window.hcaptcha.reset(captchaWidgetId.current);
            setCaptchaToken('');
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!username.trim() || !password.trim()) {
            setError('Please enter both username and password');
            return;
        }
        if (HCAPTCHA_ENABLED && !captchaToken) {
            setError('Please complete the captcha verification');
            return;
        }
        setLoading(true);
        setError('');
        try {
            await login(username.trim(), password, HCAPTCHA_ENABLED ? captchaToken : '');
        } catch (err) {
            setError(err.message || 'Login failed');
            resetCaptcha();
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{
            background: 'var(--neu-bg)',
        }}>
            {/* Subtle decorative blobs */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-30" style={{
                    background: 'radial-gradient(circle, rgba(15,108,189,0.10), transparent 70%)',
                }} />
                <div className="absolute -bottom-48 -left-32 w-[500px] h-[500px] rounded-full opacity-25" style={{
                    background: 'radial-gradient(circle, rgba(15,118,110,0.12), transparent 70%)',
                }} />
            </div>

            <div className="relative w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-10">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl mb-5 transition-transform duration-300 hover:scale-105" style={{
                        background: '#0f6cbd',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                    }}>
                        <Database className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">Banyan AI</h1>
                    <p className="text-slate-500 mt-1 font-medium">Product Data Automation Platform</p>
                </div>

                {/* Login Card — Neumorphic */}
                <div className="p-8 rounded-3xl" style={{
                    background: 'var(--neu-bg)',
                    boxShadow: '10px 10px 20px 0 rgba(163,177,198,0.7), -10px -10px 20px 0 rgba(255,255,255,0.85)',
                }}>
                    <div className="mb-7">
                        <h2 className="text-xl font-bold text-slate-800">Welcome back</h2>
                        <p className="text-slate-500 text-sm mt-1">Sign in to your account to continue</p>
                    </div>

                    {error && (
                        <div className="mb-5 px-4 py-3 rounded-2xl text-sm font-medium text-red-600" style={{
                            background: 'var(--neu-bg)',
                            boxShadow: 'inset 3px 3px 7px rgba(163,177,198,0.5), inset -3px -3px 7px rgba(255,255,255,0.6)',
                        }}>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Username */}
                        <div>
                            <label className="block text-sm font-bold text-slate-600 mb-2" htmlFor="login-username">
                                Username
                            </label>
                            <input
                                id="login-username"
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="Enter your username"
                                autoComplete="username"
                                className="w-full px-4 py-3.5 rounded-2xl text-slate-800 placeholder:text-slate-400 transition-all duration-200 focus:outline-none"
                                style={{
                                    background: 'var(--neu-bg)',
                                    boxShadow: 'inset 3px 3px 7px rgba(163,177,198,0.55), inset -3px -3px 7px rgba(255,255,255,0.7)',
                                    border: 'none',
                                }}
                                onFocus={(e) => e.target.style.boxShadow = 'inset 0 -2px 0 #0f6cbd, 0 0 0 2px rgba(15,108,189,0.16)'}
                                onBlur={(e) => e.target.style.boxShadow = 'inset 3px 3px 7px rgba(163,177,198,0.55), inset -3px -3px 7px rgba(255,255,255,0.7)'}
                            />
                        </div>

                        {/* Password */}
                        <div>
                            <label className="block text-sm font-bold text-slate-600 mb-2" htmlFor="login-password">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    id="login-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    autoComplete="current-password"
                                    className="w-full px-4 py-3.5 pr-12 rounded-2xl text-slate-800 placeholder:text-slate-400 transition-all duration-200 focus:outline-none"
                                    style={{
                                        background: 'var(--neu-bg)',
                                        boxShadow: 'inset 3px 3px 7px rgba(163,177,198,0.55), inset -3px -3px 7px rgba(255,255,255,0.7)',
                                        border: 'none',
                                    }}
                                    onFocus={(e) => e.target.style.boxShadow = 'inset 0 -2px 0 #0f6cbd, 0 0 0 2px rgba(15,108,189,0.16)'}
                                    onBlur={(e) => e.target.style.boxShadow = 'inset 3px 3px 7px rgba(163,177,198,0.55), inset -3px -3px 7px rgba(255,255,255,0.7)'}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-xl text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        {/* hCaptcha */}
                        {HCAPTCHA_ENABLED && (
                            <div className="flex justify-center">
                                <div ref={captchaRef}></div>
                            </div>
                        )}

                        {/* Submit */}
                        <button
                            id="login-submit"
                            type="submit"
                            disabled={loading || (HCAPTCHA_ENABLED && (!captchaToken || !HCAPTCHA_SITE_KEY))}
                            className="w-full py-3.5 rounded-2xl text-white font-bold text-base tracking-wide transition-all duration-200 flex items-center justify-center gap-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                                background: '#0f6cbd',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.14)',
                                border: 'none',
                                position: 'relative',
                                overflow: 'hidden',
                            }}
                            onMouseDown={(e) => {
                                if (!loading) e.currentTarget.style.boxShadow = 'inset 3px 3px 7px rgba(0,0,0,0.15), inset -3px -3px 7px rgba(255,255,255,0.1)';
                            }}
                            onMouseUp={(e) => {
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.14)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.14)';
                            }}
                        >
                            {loading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <LogIn className="w-5 h-5" />
                            )}
                            {loading ? 'Signing in...' : 'Sign In'}
                        </button>
                    </form>
                </div>

                {/* Footer */}
                <p className="text-center text-xs text-slate-400 mt-8 font-medium">
                    Banyan AI v6.9 — Joy Innovations Pvt Ltd
                </p>
            </div>
        </div>
    );
}

export default LoginPage;
